import { useState } from "react";
import { Button, Facts, FormRow, FormSection, formatDate } from "@omnabase/ui";
import { DEFAULT_PLANS, PLAN_IDS, formatStorage, type AdminWorkspace, type PlanId, type PlanLimits } from "@omnabase/shared";

/** The back office's "Plan" (M7 round one; the connector tags round, 2026-10-27: the plan's limits beside the picker, read again after a move):
 *  the catalogue version the workspace sits on, its limits, and "Move to plan". The move sets the plan and its limits at once; Stripe is never
 *  touched (a Stripe-subscribed workspace is refused by the server). */
const PLAN_NAMES = Object.fromEntries(DEFAULT_PLANS.map((p) => [p.id, p.name])) as Record<PlanId, string>;
const n = (x: number | null) => (x === null ? "no cap" : x.toLocaleString("en-US"));

export function PlanSection({ plan, busy, onMove }: { plan: AdminWorkspace["plan"]; busy: boolean; onMove: (to: PlanId) => void }) {
  const [moveTo, setMoveTo] = useState<PlanId>("free");
  const since = `${plan.retiredAt ? " (a retired version)" : ""} · since ${formatDate(plan.validFrom)}`; // the summary cards round: the bold name's row carries its words as hover text
  return (
    <FormSection title="Plan" intro="The catalogue version the workspace sits on; a move takes effect at once, is audited and mails every owner.">
      <Facts rows={[{ k: "Plan", title: `${plan.name}${since}`, v: <><strong>{plan.name}</strong>{since}</> }]} />
      {plan.limits && <p className="ob-admin-plan__limits" aria-label="The plan's limits">{limitRows(plan.limits).map((r) => `${r.k} ${r.v}`).join(" · ")}</p>}
      <FormRow label="Move to plan" help="Sets the plan and its limits here; Stripe is not changed." inline>
        <span className="ob-admin-plan__move">
          <select className="ob-select" aria-label="Plan" value={moveTo} onChange={(e) => setMoveTo(e.target.value as PlanId)}>{PLAN_IDS.map((id) => <option key={id} value={id}>{PLAN_NAMES[id]}</option>)}</select>
          <Button size="sm" disabled={busy || moveTo === plan.id} onClick={() => onMove(moveTo)}>Move to plan</Button>
        </span>
      </FormRow>
    </FormSection>
  );
}

/** the plan's limits as the page lists them */
const limitRows = (l: PlanLimits) => [
  { k: "Members", v: n(l.members) }, { k: "Projects", v: n(l.projects) }, { k: "Tickets a day before an alert", v: n(l.ticketsDayAlert) },
  { k: "Mail a day", v: n(l.mailOutDay) }, { k: "Mail a month", v: n(l.mailOutMonth) }, { k: "Knowledge words per project", v: n(l.kbWordsPerProject) },
  { k: "Storage", v: formatStorage(l.storageBytes) }, // the files round: GB, one decimal under 10
];
